/**
 * TipTap's getHTML() serializes empty paragraphs as `<p></p>`. Browsers give
 * those zero height, so blank lines disappear in read-only HTML and after save.
 * Use a non-breaking space so vertical gaps match the editor and round-trips.
 *
 * Legacy Lexical/Draft.js bodies often used `<p><br></p>` (or `&nbsp;` only) for
 * the same visual "empty" paragraph. Normalizing those to the same stored shape
 * avoids false "dirty" state when opening old records in the TipTap editor.
 */
export function normalizeRichEditorHtmlForStorage(html: string): string {
  // `[^>]*` on `<p` covers Lexical/Draft/TinyMCE classes on paragraphs.
  // `br[^>]*` covers `<br>`, `<br/>`, `<br />`, and bogus/metadata attributes.
  return html.replace(
    /<p\b[^>]*>(?:\s|<br[^>]*\/?>|&nbsp;|&#160;|\u00a0|\u200b)*<\/p>/gi,
    '<p>&nbsp;</p>'
  )
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/**
 * Converts a plain-text string to semantic HTML suitable for the rich editor.
 *
 * - Double (or more) newlines \u2192 paragraph breaks.
 * - Single newlines within a paragraph \u2192 `<br>`.
 * - HTML special characters are escaped so they render literally.
 */
export function plainTextToRichEditorHtml(text: string): string {
  const paragraphs = text.split(/\n{2,}/)
  return paragraphs
    .map((para) => {
      const inner = para
        .split('\n')
        .map((line) => escapeHtml(line))
        .join('<br>')
      return `<p>${inner}</p>`
    })
    .join('')
}

/**
 * Prepares any stored value \u2014 plain text or HTML \u2014 for display in the rich
 * editor. HTML is detected by the presence of a tag-like `<\u2026>` pattern; all
 * other values are treated as plain text and converted to paragraph HTML.
 */
export function prepareRichEditorContent(value: string | undefined): string {
  if (!value?.trim()) return '<p></p>'
  const isHtml = /<[a-z][\s\S]*?>/i.test(value)
  return isHtml
    ? normalizeRichEditorHtmlForStorage(value)
    : plainTextToRichEditorHtml(value)
}
